import { getFunctions, httpsCallable } from 'firebase/functions';
import type { PartnerProjectView, ProjectUpdate } from '@/types';
import { createLogger } from '@/utils/debug';

const functions = getFunctions();
const log = createLogger('PartnerProjects');

// ===========================================
// Types
// ===========================================

interface GetProjectsInput {
  sessionToken: string;
}

interface GetProjectsOutput {
  success: boolean;
  projects?: PartnerProjectView[];
  error?: string;
}

interface GetProjectDetailInput {
  sessionToken: string;
  projectId: string;
}

interface GetProjectDetailOutput {
  success: boolean;
  project?: PartnerProjectView;
  error?: string;
}

interface GetUpdatesInput {
  sessionToken: string;
  projectId: string;
}

interface GetUpdatesOutput {
  success: boolean;
  updates?: ProjectUpdate[];
  error?: string;
}

interface SubmitNoteInput {
  sessionToken: string;
  projectId: string;
  noteContent: string;
  mentionAssigned?: boolean;
}

interface SubmitNoteOutput {
  success: boolean;
  updateId?: string;
  error?: string;
}

// ===========================================
// API Functions
// ===========================================

/**
 * Get all projects for the partner's company
 */
export async function getPartnerProjects(
  sessionToken: string
): Promise<GetProjectsOutput> {
  return log.operation('getPartnerProjects', async () => {
    const getProjects = httpsCallable<GetProjectsInput, GetProjectsOutput>(
      functions,
      'crm_getPartnerProjects'
    );

    const result = await getProjects({ sessionToken });
    log.debug('getPartnerProjects result', { success: result.data.success, count: result.data.projects?.length ?? 0 });
    return result.data;
  });
}

/**
 * Get details for a specific project
 */
export async function getPartnerProjectDetail(
  sessionToken: string,
  projectId: string
): Promise<GetProjectDetailOutput> {
  return log.operation('getPartnerProjectDetail', async () => {
    const getDetail = httpsCallable<GetProjectDetailInput, GetProjectDetailOutput>(
      functions,
      'crm_getPartnerProjectDetail'
    );

    const result = await getDetail({ sessionToken, projectId });
    log.debug('getPartnerProjectDetail result', { success: result.data.success, projectId });
    return result.data;
  }, { projectId });
}

/**
 * Get updates/notes for a specific project
 */
export async function getPartnerProjectUpdates(
  sessionToken: string,
  projectId: string
): Promise<GetUpdatesOutput> {
  return log.operation('getPartnerProjectUpdates', async () => {
    const getUpdates = httpsCallable<GetUpdatesInput, GetUpdatesOutput>(
      functions,
      'crm_getPartnerProjectUpdates'
    );

    const result = await getUpdates({ sessionToken, projectId });
    log.debug('getPartnerProjectUpdates result', { success: result.data.success, projectId, count: result.data.updates?.length ?? 0 });
    return result.data;
  }, { projectId });
}

/**
 * Submit a note to the project
 */
export async function submitPartnerNote(
  sessionToken: string,
  projectId: string,
  noteContent: string,
  mentionAssigned: boolean = true
): Promise<SubmitNoteOutput> {
  return log.operation('submitPartnerNote', async () => {
    const submitNote = httpsCallable<SubmitNoteInput, SubmitNoteOutput>(
      functions,
      'crm_submitPartnerNote'
    );

    const result = await submitNote({
      sessionToken,
      projectId,
      noteContent,
      mentionAssigned,
    });

    log.info('submitPartnerNote result', { success: result.data.success, projectId, updateId: result.data.updateId });
    return result.data;
  }, { projectId, mentionAssigned });
}
